import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "../../lib/api";
import { useSupplier } from "../../context/SupplierContext";
import HubModeNotice from "../../components/HubModeNotice";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  ShoppingCart, Plus, Minus, Trash2, ArrowRight, ShoppingBag, FileUp,
  Package, Building2, Lock, ArrowLeft, Check, Download, FileText, Clock,
  CheckCircle2, X, Eye, Sparkles,
} from "lucide-react";
import {
  getCart, setCart as writeCart, removeFromCart, updateQty, clearCart, effectivePrice,
} from "../../lib/cart";

// ─────────────────────────────────────────────────────────────────────────────
// BuyerCart — Step 2 of the order journey
// Buyer reviews items added from catalog or PO upload, then places the order.
// Payment terms / shipping method are decided AFTER seller confirms the order.
// ─────────────────────────────────────────────────────────────────────────────

export default function BuyerCart() {
  const nav = useNavigate();
  const { active, activeSellerId, activeSellerName } = useSupplier();
  const [rawItems, setRawItems] = useState(getCart());
  const [placing, setPlacing] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // { orders: [{order, invoice}], placed_at }
  const [sellerTerms, setSellerTerms] = useState([]); // free terms granted by suppliers

  useEffect(() => {
    const refresh = () => setRawItems(getCart());
    window.addEventListener("verqo-cart-change", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("verqo-cart-change", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  // Pull seller-granted free credit lines for this buyer, if any
  useEffect(() => {
    api.get("/buyer/seller-terms").then(r => setSellerTerms(r.data)).catch(() => {});
  }, []);

  // Scope items to active supplier workspace (strict isolation).
  // In hub mode (no active supplier) show everything.
  const items = useMemo(() => {
    if (!activeSellerId) return rawItems;
    return rawItems.filter(x => x.seller_id === activeSellerId);
  }, [rawItems, activeSellerId]);

  // Items belonging to *other* suppliers — kept in storage but hidden in the
  // current workspace; surfaced as a soft indicator so buyers aren't confused.
  const hiddenOtherSupplierCount = activeSellerId
    ? rawItems.filter(x => x.seller_id !== activeSellerId).length
    : 0;

  const subtotal = items.reduce((s, x) => s + x.qty * effectivePrice(x), 0);
  const totalUnits = items.reduce((s, x) => s + x.qty, 0);
  const sellersCount = new Set(items.map(x => x.seller_id)).size;
  const sellerOfferFor = (sid) => sellerTerms.find(o => o.status === "active" && o.seller_id === sid);

  const inc = (pid) => { const it = rawItems.find(x => x.product_id === pid); updateQty(pid, (it?.qty || 0) + 1); setRawItems(getCart()); };
  const dec = (pid) => { const it = rawItems.find(x => x.product_id === pid); updateQty(pid, Math.max(it?.moq || 1, (it?.qty || 0) - 1)); setRawItems(getCart()); };
  const drop = (pid) => { removeFromCart(pid); setRawItems(getCart()); };

  const continueToPo = (sellerId) => {
    // Filter items down to the seller this PO is for (single-seller per PO).
    const arr = items.filter(it => it.seller_id === sellerId);
    if (arr.length === 0) return;
    const offer = sellerOfferFor(sellerId);
    const presetTerms = (offer && offer.terms && offer.terms !== "prepaid") ? offer.terms : "net30";
    nav("/buyer/po-generator", {
      state: {
        fromCart: true,
        seller_id: sellerId,
        terms: presetTerms,
        items: arr.map(i => ({
          product_id: i.product_id,
          sku: i.sku || "",
          name: i.name,
          qty: i.qty,
          price: effectivePrice(i),
          seller_id: i.seller_id,
          seller_name: i.seller_name,
          image: i.image,
          moq: i.moq,
        })),
      },
    });
  };

  const placeOrder = async () => {
    if (items.length === 0) return;
    setPlacing(true);
    try {
      // Group by seller so each seller gets its own order
      const bySeller = {};
      items.forEach(it => { (bySeller[it.seller_id] ??= []).push(it); });
      const created = [];
      for (const [seller_id, arr] of Object.entries(bySeller)) {
        // Phase SC-Terms: auto-pop per-seller terms from the active offer.
        // Falls back to "prepaid" when the seller hasn't offered credit —
        // backend enforces this same rule.
        const offer = sellerOfferFor(seller_id);
        const terms = (offer && offer.terms && offer.terms !== "prepaid") ? offer.terms : "prepaid";
        const { data } = await api.post("/orders", {
          seller_id,
          terms,
          items: arr.map(i => ({ product_id: i.product_id, name: i.name, qty: i.qty, price: effectivePrice(i) })),
        });
        created.push(data);
      }
      // Only clear the items we actually placed (keep other-supplier items in storage)
      if (activeSellerId) {
        const remaining = rawItems.filter(x => x.seller_id !== activeSellerId);
        writeCart(remaining);
        setRawItems(remaining);
      } else {
        clearCart();
        setRawItems([]);
      }
      setConfirmation({ orders: created, placed_at: new Date().toISOString() });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="buyer-cart">
      {/* Top header */}
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-20 px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => nav("/buyer/catalog")} className="text-foreground/60 hover:text-foreground inline-flex items-center text-sm" data-testid="back-to-catalog">
            <ArrowLeft className="w-4 h-4 mr-1.5"/>Back to catalog
          </button>
          <div className="h-6 w-px bg-border"/>
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Step 2 of 6 · Commerce</div>
            <h1 className="display text-2xl mt-1 tracking-tight">Review &amp; confirm your order</h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => nav("/buyer/catalog")} className="rounded-full h-10 px-4 hidden md:inline-flex">
            <ShoppingBag className="w-4 h-4 mr-1.5"/>Add more items
          </Button>
        </div>
      </div>

      <div className="page-shell grid lg:grid-cols-3 gap-8">

        {!activeSellerId && items.length === 0 && (
          <div className="lg:col-span-3">
            <HubModeNotice
              page="Cart"
              description="Your cart fills up when you order from a connected supplier's catalog. Until then, the equivalent flow is to draft and send POs from the Hub — works with any supplier."
            />
          </div>
        )}

        {/* ─── Items list ─── */}
        <div className="lg:col-span-2">
          {items.length === 0 ? (
            <div className="rounded-2xl bg-white border border-border p-12 text-center" data-testid="cart-empty">
              <div className="w-14 h-14 rounded-2xl bg-secondary grid place-items-center mx-auto">
                <ShoppingCart className="w-6 h-6 text-foreground/40"/>
              </div>
              <h2 className="display text-2xl mt-5">
                {activeSellerId
                  ? `No ${activeSellerName || "supplier"} items in your cart`
                  : "Your cart is empty"}
              </h2>
              <p className="text-sm text-foreground/55 mt-2 max-w-sm mx-auto">
                {activeSellerId
                  ? hiddenOtherSupplierCount > 0
                    ? `You have ${hiddenOtherSupplierCount} item${hiddenOtherSupplierCount !== 1 ? "s" : ""} in other supplier workspaces. Switch workspace to view them, or add products from ${activeSellerName || "this supplier"}.`
                    : `Add products from ${activeSellerName || "this supplier"}'s catalogue, or upload a Purchase Order — the AI will populate your cart for you.`
                  : "Add products from the catalogue, or upload a Purchase Order — the AI will populate your cart for you."}
              </p>
              <div className="mt-6 flex gap-2 justify-center">
                <Button onClick={() => nav("/buyer/catalog")} data-testid="empty-browse"
                  className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 px-5">
                  <ShoppingBag className="w-4 h-4 mr-1.5"/>Browse catalogue
                </Button>
                <Button variant="outline" onClick={() => nav("/buyer/orders#journey")} data-testid="empty-po"
                  className="rounded-full h-11 px-5">
                  <FileUp className="w-4 h-4 mr-1.5"/>Upload PO
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50">
                  {totalUnits} units · {sellersCount} supplier{sellersCount !== 1 ? "s" : ""}
                  {activeSellerId && <span className="ml-2 text-foreground/70">· scoped to {activeSellerName}</span>}
                </div>
                {hiddenOtherSupplierCount > 0 && (
                  <div className="text-[11px] text-amber-700 inline-flex items-center gap-1.5" data-testid="cart-hidden-notice">
                    <Building2 className="w-3 h-3"/>
                    {hiddenOtherSupplierCount} item{hiddenOtherSupplierCount !== 1 ? "s" : ""} hidden in other supplier workspaces
                  </div>
                )}
              </div>

              {/* Per-supplier free credit banners */}
              {(() => {
                const supplierIds = [...new Set(items.map(i => i.seller_id))];
                const banners = supplierIds
                  .map(sid => ({ sid, name: items.find(i => i.seller_id === sid)?.seller_name, offer: sellerOfferFor(sid) }))
                  .filter(b => b.offer);
                if (banners.length === 0) return null;
                return (
                  <div className="space-y-2 mb-3" data-testid="supplier-offer-banners">
                    {banners.map(({ sid, name, offer }) => (
                      <div key={sid} data-testid={`offer-banner-${sid}`}
                        className="rounded-xl border border-emerald-200 bg-emerald-50/40 px-4 py-2.5 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white grid place-items-center shrink-0">
                          <Sparkles className="w-3.5 h-3.5"/>
                        </div>
                        <div className="flex-1 min-w-0 text-sm">
                          <span className="font-medium">{offer.seller_name || name}</span>
                          <span className="text-foreground/65"> offers you free {offer.terms.toUpperCase()} </span>
                          <span className="font-medium">up to {money(offer.limit)}</span>
                          <span className="text-foreground/55"> · {money(offer.available)} available</span>
                        </div>
                        <span className="text-[10px] mono uppercase tracking-[0.14em] text-emerald-700 shrink-0">No fee · No application</span>
                      </div>
                    ))}
                  </div>
                );
              })()}

              <div className="rounded-2xl bg-white border border-border divide-y divide-border" data-testid="cart-items">
                {items.map(it => (
                  <CartRow key={it.product_id} item={it} onInc={inc} onDec={dec} onRemove={drop}/>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ─── Summary ─── */}
        {items.length > 0 && (
          <aside className="lg:col-span-1">
            <div className="rounded-2xl border border-border bg-white sticky top-24" data-testid="cart-summary">
              <div className="p-6 border-b border-border">
                <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">Order summary</div>
                <div className="display text-4xl mt-3 tracking-[-0.02em]">{money(subtotal)}</div>
                <div className="text-xs text-foreground/55 mt-1.5">Subtotal · shipping calculated after seller confirms</div>
              </div>
              <div className="p-6 space-y-3 text-sm">
                <SummaryRow l="Items" v={`${totalUnits}`}/>
                <SummaryRow l="Suppliers" v={`${sellersCount}`}/>
                <SummaryRow l="Subtotal" v={money(subtotal)}/>
                <SummaryRow l="Shipping" v="TBD" muted/>
                {/* Phase SC-Terms · Per-supplier terms preview (auto-popped from offers) */}
                {(() => {
                  const supplierIds = [...new Set(items.map(i => i.seller_id))];
                  return supplierIds.map(sid => {
                    const name = items.find(i => i.seller_id === sid)?.seller_name || "Supplier";
                    const offer = sellerOfferFor(sid);
                    const willUse = (offer && offer.terms && offer.terms !== "prepaid") ? offer.terms : "prepaid";
                    const label = willUse === "prepaid" ? "Prepay" : willUse.toUpperCase().replace("NET", "Net-");
                    return (
                      <div key={`terms-${sid}`} className="flex items-center justify-between text-[12px]"
                        data-testid={`cart-terms-row-${sid}`}>
                        <span className="text-foreground/55 truncate pr-2">{sellersCount > 1 ? `${name} terms` : "Payment terms"}</span>
                        <span className={`mono tabular-nums px-2 py-0.5 rounded ${
                          willUse === "prepaid" ? "bg-secondary text-foreground/70" : "bg-emerald-50 text-emerald-800"
                        }`}>{label}</span>
                      </div>
                    );
                  });
                })()}
                <div className="border-t border-border pt-3 mt-3 flex justify-between text-base font-medium">
                  <span>Total estimate</span>
                  <span className="display">{money(subtotal)}</span>
                </div>
              </div>
              <div className="p-6 pt-0">
                {(() => {
                  const supplierIds = [...new Set(items.map(i => i.seller_id))];
                  if (supplierIds.length <= 1) {
                    const sid = supplierIds[0];
                    return (
                      <Button onClick={() => continueToPo(sid)} disabled={!sid} data-testid="continue-to-po"
                        className="w-full h-12 rounded-full bg-foreground text-background hover:bg-foreground/90">
                        Continue to generate PO <ArrowRight className="w-4 h-4 ml-1.5"/>
                      </Button>
                    );
                  }
                  // Multi-supplier cart — one PO per supplier (POs are
                  // single-supplier artefacts). Render N stacked buttons.
                  return (
                    <div className="space-y-2" data-testid="continue-to-po-multi">
                      <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mb-1">
                        Generate one PO per supplier
                      </div>
                      {supplierIds.map(sid => {
                        const name = items.find(i => i.seller_id === sid)?.seller_name || "Supplier";
                        const supplierItems = items.filter(i => i.seller_id === sid).length;
                        return (
                          <Button key={sid} onClick={() => continueToPo(sid)} data-testid={`continue-to-po-${sid}`}
                            className="w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90 justify-between px-5">
                            <span className="inline-flex items-center gap-2">
                              <Building2 className="w-3.5 h-3.5"/>{name}
                              <span className="text-[10px] opacity-70">· {supplierItems} item{supplierItems !== 1 ? "s" : ""}</span>
                            </span>
                            <ArrowRight className="w-4 h-4"/>
                          </Button>
                        );
                      })}
                    </div>
                  );
                })()}
                <div className="mt-3 flex items-center justify-center gap-3 text-[10px] text-foreground/45">
                  <span className="inline-flex items-center gap-1"><Lock className="w-2.5 h-2.5"/>SSL</span>
                  <span>•</span>
                  <span>You&apos;ll confirm terms, shipping and delivery date on the PO</span>
                </div>
              </div>
            </div>

            {/* What happens next */}
            <div className="mt-6 rounded-2xl border border-border bg-secondary/30 p-5">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 mb-3">What happens next</div>
              <ol className="space-y-3 text-sm">
                {[
                  { n: 3, t: "Seller confirms order", d: "Usually within 2 business hours" },
                  { n: 4, t: "Invoice issued · you pay", d: "Wallet · card · ACH · wire · Net-30" },
                  { n: 5, t: "Seller packs & weighs", d: "Dimensions sent for shipping rates" },
                  { n: 6, t: "Schedule shipment", d: "Compare rates · book · track" },
                ].map(s => (
                  <li key={s.n} className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-foreground text-background grid place-items-center text-[11px] font-medium shrink-0">{s.n}</div>
                    <div>
                      <div className="font-medium text-[13px]">{s.t}</div>
                      <div className="text-[11px] text-foreground/55 mt-0.5">{s.d}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        )}
      </div>

      <OrderConfirmationModal
        open={!!confirmation}
        data={confirmation}
        onClose={() => setConfirmation(null)}
        onTrack={() => { setConfirmation(null); nav("/buyer/orders#journey"); }}
        onAddAnother={() => { setConfirmation(null); nav("/buyer/catalog"); }}
      />
    </div>
  );
}

function CartRow({ item, onInc, onDec, onRemove }) {
  const eff = effectivePrice(item);
  const lineTotal = item.qty * eff;
  const isTier = eff < (item.price || 0);
  const tiers = item.tiers || [];
  // Find the next tier the buyer could unlock by adding more units.
  const nextTier = tiers.find(t => t.min_qty > item.qty);
  return (
    <div className="grid grid-cols-12 gap-4 items-center p-5" data-testid={`cart-row-${item.product_id}`}>
      <div className="col-span-6 flex items-center gap-4 min-w-0">
        <div className="w-14 h-14 rounded-xl bg-secondary grid place-items-center shrink-0">
          {item.image
            ? <img src={item.image} alt={item.name} className="w-full h-full object-cover rounded-xl"/>
            : <Package className="w-5 h-5 text-foreground/40"/>}
        </div>
        <div className="min-w-0">
          <div className="font-medium truncate">{item.name}</div>
          <div className="text-xs text-foreground/55 mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
            <Building2 className="w-3 h-3"/>{item.seller_name}
            {item.moq && <span className="ml-2 mono text-[10px] text-foreground/40">MOQ {item.moq}</span>}
            {isTier && (
              <span className="ml-1 text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                volume tier
              </span>
            )}
          </div>
          {nextTier && (
            <div className="text-[10px] text-foreground/55 mt-1">
              + {nextTier.min_qty - item.qty} more to unlock {money(nextTier.price)}/ea
            </div>
          )}
        </div>
      </div>

      <div className="col-span-3 flex items-center justify-center gap-1">
        <button onClick={() => onDec(item.product_id)} className="w-8 h-8 rounded-full border border-border hover:bg-secondary grid place-items-center" data-testid={`dec-${item.product_id}`}>
          <Minus className="w-3.5 h-3.5"/>
        </button>
        <Input
          type="number"
          value={item.qty}
          onChange={e => updateQty(item.product_id, Math.max(item.moq || 1, parseInt(e.target.value) || 1))}
          className="w-16 h-8 text-center mono"
          data-testid={`qty-${item.product_id}`}
        />
        <button onClick={() => onInc(item.product_id)} className="w-8 h-8 rounded-full border border-border hover:bg-secondary grid place-items-center" data-testid={`inc-${item.product_id}`}>
          <Plus className="w-3.5 h-3.5"/>
        </button>
      </div>

      <div className="col-span-2 text-right">
        <div className="display text-base">{money(lineTotal)}</div>
        <div className="text-[10px] mono mt-0.5">
          <span className={isTier ? "text-emerald-700" : "text-foreground/50"}>{money(eff)} ea</span>
          {isTier && <span className="ml-1 line-through text-foreground/35">{money(item.price)}</span>}
        </div>
      </div>

      <div className="col-span-1 text-right">
        <button onClick={() => onRemove(item.product_id)} className="text-foreground/40 hover:text-rose-700 transition-colors" data-testid={`remove-${item.product_id}`}>
          <Trash2 className="w-4 h-4"/>
        </button>
      </div>
    </div>
  );
}

function SummaryRow({ l, v, muted }) {
  return (
    <div className={`flex justify-between ${muted ? "text-foreground/45" : "text-foreground/70"}`}>
      <span>{l}</span>
      <span className="mono">{v}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OrderConfirmationModal — large, post-checkout success dialog.
// Shows the buyer their PO number(s), invoice number(s), expected confirmation
// date by the seller, and lets them download a PO/SO PDF, track the order,
// or place another.
// ─────────────────────────────────────────────────────────────────────────────
function OrderConfirmationModal({ open, data, onClose, onTrack, onAddAnother }) {
  if (!data) return null;
  const orders = data.orders || [];
  const totalAmount = orders.reduce((s, x) => s + (x.order?.total || 0), 0);
  const sellersCount = orders.length;
  // Earliest expected seller confirmation
  const expectedAt = orders
    .map(x => x.order?.expected_invoice_at)
    .filter(Boolean)
    .sort()[0];
  const expectedLabel = expectedAt
    ? new Date(expectedAt).toLocaleString(undefined, {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : "Within 2 business hours";

  const handleDownload = () => {
    // Build a printable HTML page in-memory and load it via a Blob URL.
    // Avoids document.write (which several security linters flag as an XSS
    // vector even when input is server-controlled). All dynamic values are
    // escaped before insertion as a defence-in-depth measure.
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    }[c]));
    const rows = orders.map(({ order, invoice }) => `
      <tr>
        <td style="padding:10px 12px;border-bottom:1px solid #eee">${esc(order.code)}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #eee">${esc(invoice.code)}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #eee">${esc(order.seller_name)}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #eee">${(order.items || []).length} items</td>
        <td style="padding:10px 12px;border-bottom:1px solid #eee;text-align:right">$${Number(order.total || 0).toLocaleString()}</td>
      </tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Sales order — ${esc(orders[0]?.order?.code)}</title>
      <style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;max-width:800px;margin:40px auto;padding:0 24px;color:#111}
      h1{font-size:24px;margin:0 0 4px} .muted{color:#666;font-size:13px}
      table{width:100%;border-collapse:collapse;margin-top:24px;font-size:14px}
      th{padding:10px 12px;background:#f7f7f7;text-align:left;font-weight:600;font-size:12px;text-transform:uppercase;letter-spacing:.05em;color:#444}
      .total{margin-top:16px;text-align:right;font-size:18px;font-weight:600}
      .badge{display:inline-block;padding:4px 10px;border-radius:999px;background:#fff7ed;color:#9a3412;font-size:11px;text-transform:uppercase;letter-spacing:.08em;margin-top:8px}
      </style></head><body>
      <h1>Sales order confirmation</h1>
      <div class="muted">Placed ${esc(new Date(data.placed_at).toLocaleString())}</div>
      <div class="badge">Awaiting seller confirmation</div>
      <p style="margin-top:16px">Expected seller confirmation: <strong>${esc(expectedLabel)}</strong></p>
      <table>
        <thead><tr><th>PO / Sales order #</th><th>Invoice #</th><th>Supplier</th><th>Items</th><th style="text-align:right">Total</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="total">Grand total · $${Number(totalAmount).toLocaleString()}</div>
      <p class="muted" style="margin-top:32px">No payment is due until the seller confirms and issues the final invoice. Track live status in your buyer portal.</p>
      </body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) {
      URL.revokeObjectURL(url);
      toast.error("Pop-up blocked — allow pop-ups to download");
      return;
    }
    // Trigger print once the new window has finished loading the blob, then
    // free the blob URL so we don't leak memory.
    w.addEventListener("load", () => {
      try { w.print(); } catch { /* user cancelled or print blocked */ }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden rounded-3xl border-border"
        data-testid="order-confirmation-modal"
      >
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-emerald-50 via-white to-amber-50 px-8 pt-10 pb-8 border-b border-border">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-foreground/5 grid place-items-center text-foreground/50"
            data-testid="close-confirmation"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="w-14 h-14 rounded-2xl bg-emerald-600 text-white grid place-items-center shadow-sm">
            <CheckCircle2 className="w-7 h-7" strokeWidth={2.2} />
          </div>
          <div className="text-[10px] mono uppercase tracking-[0.22em] text-emerald-700/80 mt-5">
            Order placed · {sellersCount} {sellersCount === 1 ? "supplier" : "suppliers"}
          </div>
          <h2 className="display text-3xl mt-2 tracking-tight">
            We've sent your {sellersCount === 1 ? "order" : "orders"} for confirmation
          </h2>
          <p className="text-sm text-foreground/65 mt-2 max-w-lg">
            Your supplier{sellersCount === 1 ? " has" : "s have"} received the purchase order. You'll receive
            the final invoice once they confirm stock and a "ready by" date — usually within 2 business hours.
            <span className="font-medium text-foreground"> No payment is due yet.</span>
          </p>

          <div className="mt-5 inline-flex items-center gap-2 bg-white border border-amber-200 rounded-full pl-3 pr-4 py-1.5 text-xs">
            <Clock className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-foreground/55">Expected confirmation</span>
            <span className="font-medium text-foreground mono">{expectedLabel}</span>
          </div>
        </div>

        {/* Order rows */}
        <div className="px-8 py-6 max-h-[280px] overflow-y-auto" data-testid="confirmation-orders">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 mb-3">
            Reference numbers
          </div>
          <div className="space-y-2">
            {orders.map(({ order, invoice }) => (
              <div
                key={order.id}
                className="rounded-xl border border-border bg-white px-4 py-3 flex items-center gap-4"
                data-testid={`confirmation-order-${order.code}`}
              >
                <div className="w-9 h-9 rounded-lg bg-secondary grid place-items-center shrink-0">
                  <FileText className="w-4 h-4 text-foreground/55" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">{order.seller_name}</div>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] mono text-foreground/55 mt-0.5">
                    <span>PO/SO <span className="text-foreground">{order.code}</span></span>
                    <span>INV <span className="text-foreground">{invoice.code}</span></span>
                    <span>{(order.items || []).length} item{(order.items || []).length !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="display text-base">${(order.total || 0).toLocaleString()}</div>
                  <div className="text-[10px] mono uppercase tracking-[0.12em] text-amber-700">Pending</div>
                </div>
              </div>
            ))}
          </div>

          {sellersCount > 1 && (
            <div className="flex justify-between items-center mt-4 px-1 text-sm">
              <span className="text-foreground/60">Grand total estimate</span>
              <span className="display text-lg">${totalAmount.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-8 pt-2 pb-8 border-t border-border bg-secondary/20">
          <div className="grid grid-cols-2 gap-2 mt-5">
            <Button
              variant="outline"
              onClick={handleDownload}
              className="h-11 rounded-full"
              data-testid="confirmation-download"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Download details
            </Button>
            <Button
              variant="outline"
              onClick={onAddAnother}
              className="h-11 rounded-full"
              data-testid="confirmation-add-another"
            >
              <ShoppingBag className="w-4 h-4 mr-1.5" />
              Place another order
            </Button>
          </div>
          <Button
            onClick={onTrack}
            className="w-full h-12 rounded-full bg-foreground text-background hover:bg-foreground/90 mt-2"
            data-testid="confirmation-track"
          >
            <Eye className="w-4 h-4 mr-1.5" />
            Track order status
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <div className="mt-3 flex items-center justify-center gap-2 text-[10px] text-foreground/45">
            <Lock className="w-2.5 h-2.5" />
            <span>You'll be notified the moment your supplier confirms.</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
